export type FundingActionStatus =
  | 'connected'
  | 'not_set'
  | 'pending'
  | 'disconnected'
  | 'needs_reauth';

export type TransferMode = 'deposit' | 'withdraw';

export type TransferActionDecision = {
  allowed: boolean;
  /** Deposit with no bank: open the add-funding popover instead of the modal. */
  openFundingFirst: boolean;
  reason: string | null;
};

export const transferActionDecision = ({
  mode,
  fundingStatus,
  bankName,
  availableToWithdraw,
  submitting = false,
}: {
  mode: TransferMode;
  fundingStatus: FundingActionStatus;
  bankName?: string;
  availableToWithdraw: number | null;
  submitting?: boolean;
}): TransferActionDecision => {
  const bank = bankName?.trim() || 'your bank';
  if (submitting) {
    return { allowed: false, openFundingFirst: false, reason: 'Please wait.' };
  }
  if (fundingStatus === 'needs_reauth' || fundingStatus === 'disconnected') {
    return {
      allowed: false,
      openFundingFirst: false,
      reason: `Reconnect ${bank} to ${mode === 'deposit' ? 'deposit' : 'withdraw'}.`,
    };
  }
  if (fundingStatus === 'pending') {
    return {
      allowed: false,
      openFundingFirst: false,
      reason: `Available once ${bank} is verified.`,
    };
  }
  if (fundingStatus === 'not_set') {
    if (mode === 'deposit') {
      return { allowed: true, openFundingFirst: true, reason: null };
    }
    return {
      allowed: false,
      openFundingFirst: false,
      reason: 'Add a funding bank to withdraw.',
    };
  }
  if (mode === 'withdraw' && availableToWithdraw != null && availableToWithdraw <= 0) {
    return {
      allowed: false,
      openFundingFirst: false,
      reason: 'No funds available to withdraw.',
    };
  }
  return { allowed: true, openFundingFirst: false, reason: null };
};

export const parseAvailableToWithdraw = (value?: string | null): number | null => {
  if (value == null || value.trim() === '') {
    return null;
  }
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

// cash_available_for_withdraw is missing on firm-data accounts; fall back to
// the account's equity so a $0 account still can't start a withdrawal.
export const withdrawableAmount = (account: {
  cash_available_for_withdraw?: string | null;
  total_equity?: number | null;
}): number | null => {
  const available = parseAvailableToWithdraw(account.cash_available_for_withdraw);
  if (available != null) {
    return available;
  }
  const equity = Number(account.total_equity);
  return account.total_equity != null && Number.isFinite(equity) && equity <= 0 ? 0 : null;
};
